import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PictureSliderComponent, PictureSlide } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { Plus, Trash2, Upload, GripVertical, Link, ExternalLink, Image } from 'lucide-react';
import toast from 'react-hot-toast';

interface PictureSliderSettingsProps {
  component: PictureSliderComponent;
}

export const PictureSliderSettings: React.FC<PictureSliderSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);

  const handleUpdate = (updates: Partial<PictureSliderComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const handleSlideUpdate = (index: number, updates: Partial<PictureSlide>) => {
    const newSlides = [...(component.settings.slides || [])];
    newSlides[index] = { ...newSlides[index], ...updates };
    handleUpdate({ slides: newSlides });
  };

  const addSlide = () => {
    const newSlide: PictureSlide = {
      id: `slide-${Date.now()}`,
      src: '',
      alt: '',
      link: '',
    };
    handleUpdate({ slides: [...(component.settings.slides || []), newSlide] });
  };

  const removeSlide = (index: number) => {
    const newSlides = component.settings.slides.filter((_, i) => i !== index);
    handleUpdate({ slides: newSlides });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('settings.common.imageFormatRestricted'));
      return;
    }

    const maxSize = file.type === 'image/gif' ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      const limit = file.type === 'image/gif'
        ? t('settings.picture.fileLimit10MB')
        : t('settings.picture.fileLimit5MB');
      toast.error(t('settings.common.fileTooLarge', { limit }));
      return;
    }

    setIsUploading(true);
    setUploadingIndex(index);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || t('settings.common.uploadFailed'));
      }

      const data = await response.json();
      handleSlideUpdate(index, { src: data.url });
      toast.success(t('settings.common.imageUploadSuccess'));
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || t('settings.common.imageUploadFailed'));
    } finally {
      setIsUploading(false);
      setUploadingIndex(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium">
            {t('settings.pictureSlider.slidesCount', { count: component.settings.slides?.length || 0 })}
          </label>
          <button
            onClick={addSlide}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors"
          >
            <Plus size={14} />
            {t('settings.pictureSlider.addSlide')}
          </button>
        </div>

        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
          {(component.settings.slides || []).map((slide, index) => (
            <div 
              key={slide.id} 
              className="bg-gray-800 rounded-lg p-3 border border-gray-700"
            >
              <div className="flex items-start gap-2 mb-2">
                <div className="text-gray-500 cursor-grab">
                  <GripVertical size={16} />
                </div>
                <span className="text-xs text-gray-400 font-medium">#{index + 1}</span>
                <button
                  onClick={() => removeSlide(index)}
                  className="ml-auto p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                  title={t('settings.pictureSlider.removeSlide')}
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Image Preview - Always show with 1:1 aspect ratio */}
              <div className="mb-2">
                <img
                  src={slide.src || 'https://placehold.co/1040x1040/1f2937/6b7280?text=1040x1040'}
                  alt={slide.alt || `Slide ${index + 1}`}
                  className="w-full aspect-square object-cover rounded-lg border border-gray-600"
                />
              </div>

              {/* Upload Button */}
              <div className="mb-2">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => handleFileUpload(e, index)}
                  className="hidden"
                  id={`upload-${slide.id}`}
                />
                <label
                  htmlFor={`upload-${slide.id}`}
                  className={`flex items-center justify-center gap-2 w-full px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-xs rounded-lg cursor-pointer transition-all ${
                    isUploading && uploadingIndex === index ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isUploading && uploadingIndex === index ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('settings.common.uploading')}
                    </>
                  ) : (
                    <>
                      <Upload size={14} />
                      {slide.src
                        ? t('settings.pictureSlider.changeImage')
                        : t('settings.pictureSlider.uploadImage')}
                    </>
                  )}
                </label>
              </div>

              {/* URL Input */}
              <div className="mb-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <Image size={12} className="text-gray-500" />
                  </div>
                  <input
                    type="text"
                    value={slide.src}
                    onChange={(e) => handleSlideUpdate(index, { src: e.target.value })}
                    className="w-full pl-7 pr-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder={t('settings.pictureSlider.imageUrlPlaceholder')}
                  />
                </div>
              </div>

              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <Link size={12} className="text-gray-500" />
                  </div>
                  <input
                    type="text"
                    value={slide.link || ''}
                    onChange={(e) => handleSlideUpdate(index, { link: e.target.value })}
                    className="w-full pl-7 pr-2 py-1.5 bg-gray-900 border border-gray-700 rounded text-white text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder={t('settings.pictureSlider.linkPlaceholderOptional')}
                  />
                </div>
              </div>
            </div>
          ))}

          {(component.settings.slides || []).length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm">
              {t('settings.pictureSlider.emptyState')}
            </div>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-2 text-center">
          {t('settings.common.imageHintFull')}
        </p>
      </div>

      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">{t('settings.pictureSlider.autoPlay')}</label>
        <button
          onClick={() => handleUpdate({ autoPlay: !component.settings.autoPlay })}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            component.settings.autoPlay ? 'bg-blue-600' : 'bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              component.settings.autoPlay ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {component.settings.autoPlay && (
        <div>
          <label className="block text-sm font-medium mb-2">
            {t('settings.pictureSlider.slideDurationSec', {
              seconds: (component.settings.autoPlaySpeed || 5000) / 1000,
            })}
          </label>
          <input
            type="range"
            min="2000"
            max="10000"
            step="500"
            value={component.settings.autoPlaySpeed || 5000}
            onChange={(e) => handleUpdate({ autoPlaySpeed: parseInt(e.target.value) })}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
          />
        </div>
      )}

      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">{t('settings.pictureSlider.showArrows')}</label>
        <button
          onClick={() => handleUpdate({ showArrows: !component.settings.showArrows })}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            component.settings.showArrows ? 'bg-blue-600' : 'bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              component.settings.showArrows ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">{t('settings.pictureSlider.showDots')}</label>
        <button
          onClick={() => handleUpdate({ showDots: !component.settings.showDots })}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            component.settings.showDots ? 'bg-blue-600' : 'bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              component.settings.showDots ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      <div className="flex items-center justify-between">
        <label className="text-sm font-medium flex items-center gap-2">
          <ExternalLink size={16} />
          {t('settings.pictureSlider.openLinksNewTab')}
        </label>
        <button
          onClick={() => handleUpdate({ openInNewTab: !component.settings.openInNewTab })}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            component.settings.openInNewTab ? 'bg-blue-600' : 'bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              component.settings.openInNewTab ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.common.borderRadiusPx', { value: component.settings.borderRadius })}
        </label>
        <input
          type="range"
          min="0"
          max="50"
          value={component.settings.borderRadius}
          onChange={(e) => handleUpdate({ borderRadius: parseInt(e.target.value) })}
          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.shadow')}</label>
        <select
          value={component.settings.shadow}
          onChange={(e) => handleUpdate({ shadow: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="none">{t('settings.common.shadows.none')}</option>
          <option value="sm">{t('settings.common.shadows.sm')}</option>
          <option value="md">{t('settings.common.shadows.md')}</option>
          <option value="lg">{t('settings.common.shadows.lg')}</option>
          <option value="xl">{t('settings.common.shadows.xl')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.common.paddingPx', { value: component.settings.padding })}
        </label>
        <input
          type="range"
          min="0"
          max="64"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
        />
      </div>
    </div>
  );
};

